import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface ControlDossieSummary {
  total: number;
  collecting: number;
  ready: number;
  organized: number;
}

export interface ControlDossieCase {
  id: number;
  tenant_id: number;
  owner_user_id: number;
  title: string;
  objective: string;
  evidence_guidance?: string | null;
  ai_prompt?: string | null;
  analysis_guidance?: string | null;
  status: string;
  controldocs_folder_id?: number | null;
  final_summary?: string | null;
  final_report?: string | null;
  last_analyzed_at?: string | null;
  evidences_count?: number;
  analyzed_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ControlDossieEvidence {
  id: number;
  dossie_id: number;
  evidence_type: string;
  title: string;
  description?: string | null;
  text_content?: string | null;
  source_url?: string | null;
  facts_date?: string | null;
  controldocs_file_id?: number | null;
  file_name?: string | null;
  file_type?: string | null;
  file_size?: number | null;
  status: string;
  created_at: string;
}

export interface ControlDossiePaged<T> {
  rows: T[];
  total: number;
  page: number;
  page_size: number;
}

@Injectable({ providedIn: 'root' })
export class ControlDossieService {
  private readonly apiUrl = `${API_BASE_URL}/controldossie`;

  constructor(private http: HttpClient) {}

  dashboard(tenantId: number, userId: number): Observable<ControlDossieSummary> {
    return this.http.get<ControlDossieSummary>(`${this.apiUrl}/dashboard`, {
      params: this.baseParams(tenantId, userId)
    });
  }

  listDossies(tenantId: number, userId: number, filters: { q?: string; status?: string; page?: number; page_size?: number } = {}): Observable<ControlDossiePaged<ControlDossieCase>> {
    let params = this.baseParams(tenantId, userId)
      .set('page', String(filters.page || 1))
      .set('page_size', String(filters.page_size || 10));
    if (filters.q) params = params.set('q', filters.q);
    if (filters.status) params = params.set('status', filters.status);
    return this.http.get<ControlDossiePaged<ControlDossieCase>>(`${this.apiUrl}/dossies`, { params });
  }

  createDossie(tenantId: number, userId: number, payload: Partial<ControlDossieCase>): Observable<{ id: number; controldocs_folder_id?: number | null }> {
    return this.http.post<{ id: number; controldocs_folder_id?: number | null }>(`${this.apiUrl}/dossies`, {
      ...payload,
      tenant_id: tenantId,
      user_id: userId,
      created_by: userId
    });
  }

  getDossie(tenantId: number, userId: number, id: number): Observable<ControlDossieCase> {
    return this.http.get<ControlDossieCase>(`${this.apiUrl}/dossies/${id}`, {
      params: this.baseParams(tenantId, userId)
    });
  }

  updateDossie(tenantId: number, userId: number, id: number, payload: Partial<ControlDossieCase>): Observable<{ ok: boolean }> {
    return this.http.put<{ ok: boolean }>(`${this.apiUrl}/dossies/${id}`, {
      ...payload,
      tenant_id: tenantId,
      user_id: userId,
      updated_by: userId
    });
  }

  updateReport(tenantId: number, userId: number, id: number, finalReport: string, status = 'ORGANIZED'): Observable<{ ok: boolean; final_report: string; final_summary?: string; status: string }> {
    return this.http.put<{ ok: boolean; final_report: string; final_summary?: string; status: string }>(`${this.apiUrl}/dossies/${id}/report`, {
      tenant_id: tenantId,
      user_id: userId,
      updated_by: userId,
      final_report: finalReport,
      status
    });
  }

  finalizeDossie(tenantId: number, userId: number, id: number): Observable<{ ok: boolean; status: string; message: string }> {
    return this.http.post<{ ok: boolean; status: string; message: string }>(`${this.apiUrl}/dossies/${id}/finalize`, {
      tenant_id: tenantId,
      user_id: userId,
      updated_by: userId
    });
  }

  deleteDossie(tenantId: number, userId: number, id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.apiUrl}/dossies/${id}`, {
      params: this.baseParams(tenantId, userId)
    });
  }

  listEvidences(tenantId: number, userId: number, dossieId: number): Observable<ControlDossieEvidence[]> {
    return this.http.get<ControlDossieEvidence[]>(`${this.apiUrl}/dossies/${dossieId}/evidences`, {
      params: this.baseParams(tenantId, userId)
    });
  }

  addEvidence(tenantId: number, userId: number, dossieId: number, payload: {
    title: string;
    evidence_type: string;
    description?: string;
    text_content?: string;
    source_url?: string;
    facts_date?: string;
    files?: File[];
  }): Observable<{ id: number; ids?: number[]; count?: number; controldocs_file_id?: number | null; controldocs_file_ids?: Array<number | null> }> {
    const form = new FormData();
    form.append('tenant_id', String(tenantId));
    form.append('user_id', String(userId));
    form.append('created_by', String(userId));
    form.append('title', payload.title || '');
    form.append('evidence_type', payload.evidence_type || 'TEXT');
    form.append('description', payload.description || '');
    form.append('text_content', payload.text_content || '');
    form.append('source_url', payload.source_url || '');
    form.append('facts_date', payload.facts_date || '');
    (payload.files || []).forEach((file) => {
      form.append('files', file, file.name);
    });
    return this.http.post<{ id: number; ids?: number[]; count?: number; controldocs_file_id?: number | null; controldocs_file_ids?: Array<number | null> }>(`${this.apiUrl}/dossies/${dossieId}/evidences`, form);
  }

  deleteEvidence(tenantId: number, userId: number, id: number): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.apiUrl}/evidences/${id}`, {
      params: this.baseParams(tenantId, userId)
    });
  }

  analyze(tenantId: number, userId: number, dossieId: number, analysisGuidance = ''): Observable<{ ok: boolean; message: string; final_report?: string; final_summary?: string; analysis_guidance?: string | null; last_analyzed_at?: string }> {
    return this.http.post<{ ok: boolean; message: string; final_report?: string; final_summary?: string; analysis_guidance?: string | null; last_analyzed_at?: string }>(`${this.apiUrl}/dossies/${dossieId}/analyze`, {
      tenant_id: tenantId,
      user_id: userId,
      analysis_guidance: analysisGuidance
    });
  }

  private baseParams(tenantId: number, userId: number): HttpParams {
    return new HttpParams()
      .set('tenant_id', String(tenantId))
      .set('user_id', String(userId));
  }
}
